import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Download } from "lucide-react";
import { api } from "@/lib/api";
import { inr } from "@/lib/site";

const STATUSES = ["New", "Contacted", "Scheduled", "Converted"];
const RATE_FIELDS = [
  { key: "gold_24k", label: "24K · 999 (per gram)" },
  { key: "gold_22k", label: "22K · 916 (per gram)" },
  { key: "gold_18k", label: "18K · 750 (per gram)" },
  { key: "silver", label: "Silver · 999 (per gram)" },
];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("aj_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [rates, setRates] = useState(null);
  const [banner, setBanner] = useState(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [l, r, b] = await Promise.all([api.get("/enquiries"), api.get("/rates"), api.get("/banner")]);
      setLeads(l.data);
      setRates(r.data);
      setBanner(b.data);
    } catch {
      localStorage.removeItem("aj_admin_token");
      setToken("");
    }
  }, []);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("aj_admin_token", res.data.access_token);
      setToken(res.data.access_token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("aj_admin_token");
    setToken("");
  };

  const setStatus = async (id, status) => {
    await api.patch(`/enquiries/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
  };

  const saveRates = async () => {
    const payload = {};
    RATE_FIELDS.forEach((f) => {
      payload[f.key] = parseFloat(rates[f.key]) || 0;
    });
    const res = await api.put("/rates", payload);
    setRates(res.data);
    setNotice("Board rates updated — live across the site.");
    setTimeout(() => setNotice(""), 3500);
  };

  const saveBanner = async () => {
    const res = await api.put("/banner", {
      enabled: banner.enabled,
      text: banner.text,
      cta_label: banner.cta_label,
      cta_target: banner.cta_target,
    });
    setBanner(res.data);
    setNotice(res.data.enabled ? "Offer strip is live on the website." : "Offer strip switched off.");
    setTimeout(() => setNotice(""), 3500);
  };

  const exportCsv = () => {
    const rows = [
      ["Created", "Name", "Phone", "Email", "Type", "Message", "Status"],
      ...leads.map((l) => [l.created_at, l.name, l.phone, l.email || "", l.kind, (l.message || "").replace(/\n/g, " "), l.status]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "aradhana-leads.csv";
    a.click();
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-[#060B18] px-4">
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="glass-card w-full max-w-sm rounded-3xl p-10"
        >
          <img src="/assets/logo-badge.png" alt="Aradhana crest" className="mx-auto h-16 w-16 rounded-2xl" />
          <h1 className="mt-6 text-center font-serif text-2xl text-white">Atelier Console</h1>
          <p className="mt-1 text-center font-mono text-[10px] tracking-[0.3em] text-[#99732B]">ARADHANAJEWELLERS.COM</p>
          <div className="mt-8 space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="lux-input"
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="lux-input"
            />
            {error && (
              <p data-testid="admin-login-error" className="text-xs text-red-400">
                {error}
              </p>
            )}
            <button data-testid="admin-login-button" type="submit" className="btn-gold w-full !py-3">
              Enter the Vault
            </button>
          </div>
        </motion.form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#060B18] px-4 py-10 text-white lg:px-12">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/assets/logo-badge.png" alt="Aradhana crest" className="h-10 w-10 rounded-lg" />
            <div>
              <h1 className="font-serif text-2xl">Atelier Console</h1>
              <p className="font-mono text-[10px] tracking-[0.3em] text-[#99732B]">LEADS & BOARD RATES</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="rounded-full border border-white/10 p-2.5 text-[#B5B0A8] transition-colors hover:border-[#D4AF37]/40 hover:text-[#E6C265]"
            >
              <RefreshCw size={15} />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-[#B5B0A8] transition-colors hover:border-[#D4AF37]/40 hover:text-[#E6C265]"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            data-testid="admin-leads-tab"
            onClick={() => setTab("leads")}
            className={`rounded-full px-5 py-2 text-xs font-medium transition-colors ${tab === "leads" ? "bg-[#D4AF37] text-black" : "border border-white/10 text-[#B5B0A8]"}`}
          >
            Leads ({leads.length})
          </button>
          <button
            data-testid="admin-rates-tab"
            onClick={() => setTab("rates")}
            className={`rounded-full px-5 py-2 text-xs font-medium transition-colors ${tab === "rates" ? "bg-[#D4AF37] text-black" : "border border-white/10 text-[#B5B0A8]"}`}
          >
            Board Rates
          </button>
          <button
            data-testid="admin-banner-tab"
            onClick={() => setTab("banner")}
            className={`rounded-full px-5 py-2 text-xs font-medium transition-colors ${tab === "banner" ? "bg-[#D4AF37] text-black" : "border border-white/10 text-[#B5B0A8]"}`}
          >
            Offer Strip
          </button>
        </div>

        {tab === "leads" && (
          <div className="mt-6">
            <div className="mb-4 flex justify-end">
              <button
                data-testid="export-csv-button"
                onClick={exportCsv}
                className="flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-xs text-[#F3E5AB] transition-colors hover:bg-[#D4AF37]/10"
              >
                <Download size={13} /> Export CSV
              </button>
            </div>
            {leads.length === 0 && (
              <p data-testid="leads-empty-state" className="rounded-2xl border border-white/5 p-10 text-center text-sm text-[#79756E]">
                No enquiries yet — they'll appear here as guests reach out.
              </p>
            )}
            <div className="space-y-3">
              {leads.map((l) => (
                <div key={l.id} data-testid={`lead-card-${l.id}`} className="glass-card rounded-2xl p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-sm font-medium text-white">
                        {l.name} <span className="ml-2 font-mono text-xs text-[#E6C265]">{l.phone}</span>
                      </p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#99732B]">{l.kind}</p>
                      {l.meta?.context && <p className="mt-2 text-xs text-[#B5B0A8]">{l.meta.context}</p>}
                      {l.message && <p className="mt-1 text-xs font-light text-[#79756E]">{l.message}</p>}
                      <p className="mt-2 font-mono text-[10px] text-[#57534A]">{new Date(l.created_at).toLocaleString("en-IN")}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {STATUSES.map((s) => (
                        <button
                          key={s}
                          data-testid={`lead-status-${s.toLowerCase()}-${l.id}`}
                          onClick={() => setStatus(l.id, s)}
                          className={`rounded-full px-3 py-1 text-[10px] transition-colors ${
                            l.status === s ? "bg-[#D4AF37] text-black" : "border border-white/10 text-[#79756E] hover:text-[#E6C265]"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "rates" && rates && (
          <div className="glass-card mt-6 max-w-xl rounded-3xl p-8">
            <div className="space-y-5">
              {RATE_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="eyebrow">{f.label}</span>
                  <input
                    data-testid={`rate-input-${f.key}`}
                    type="number"
                    step="0.5"
                    value={rates[f.key]}
                    onChange={(e) => setRates({ ...rates, [f.key]: e.target.value })}
                    className="lux-input mt-2 font-mono"
                  />
                </label>
              ))}
              <button data-testid="rates-save-button" onClick={saveRates} className="btn-gold w-full !py-3">
                Publish Board Rates
              </button>
              {notice && (
                <p data-testid="rates-saved-notice" className="text-center text-xs text-[#E6C265]">
                  {notice}
                </p>
              )}
              <p className="text-center font-mono text-[10px] text-[#57534A]">
                Last updated · {rates.updated_at ? new Date(rates.updated_at).toLocaleString("en-IN") : "—"}
              </p>
            </div>
          </div>
        )}

        {tab === "banner" && banner && (
          <div data-testid="banner-panel" className="glass-card mt-6 max-w-xl rounded-3xl p-8">
            <div className="space-y-5">
              <button
                data-testid="banner-toggle-button"
                onClick={() => setBanner({ ...banner, enabled: !banner.enabled })}
                className="flex w-full items-center justify-between rounded-2xl border border-white/10 px-5 py-4 text-left"
              >
                <span>
                  <span className="eyebrow">Festival offer strip</span>
                  <span className="mt-1 block text-xs font-light text-[#79756E]">
                    Shows above the gold-rate bar on every page
                  </span>
                </span>
                <span
                  className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${banner.enabled ? "bg-[#D4AF37]" : "bg-white/15"}`}
                >
                  <span
                    className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${banner.enabled ? "left-6" : "left-1"}`}
                  />
                </span>
              </button>
              <label className="block">
                <span className="eyebrow">Offer message</span>
                <textarea
                  data-testid="banner-text-input"
                  rows={2}
                  value={banner.text}
                  onChange={(e) => setBanner({ ...banner, text: e.target.value })}
                  className="lux-input mt-2 resize-none"
                />
              </label>
              <label className="block">
                <span className="eyebrow">Button label (optional)</span>
                <input
                  data-testid="banner-cta-label-input"
                  value={banner.cta_label || ""}
                  onChange={(e) => setBanner({ ...banner, cta_label: e.target.value })}
                  className="lux-input mt-2"
                />
              </label>
              <label className="block">
                <span className="eyebrow">Button goes to</span>
                <select
                  data-testid="banner-cta-target-input"
                  value={banner.cta_target || "contact"}
                  onChange={(e) => setBanner({ ...banner, cta_target: e.target.value })}
                  className="lux-input mt-2"
                >
                  <option value="contact">Visit Us / booking form</option>
                  <option value="collections">Collections</option>
                  <option value="rates">Gold Rates</option>
                  <option value="scheme">Swarna Nidhi scheme</option>
                </select>
              </label>
              <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-r from-[#8C6A15] via-[#D4AF37] to-[#8C6A15] px-4 py-2.5">
                <p data-testid="banner-preview" className="truncate text-center text-xs font-medium text-[#231502]">
                  {banner.text || "Your offer message appears here"}
                </p>
              </div>
              <button data-testid="banner-save-button" onClick={saveBanner} className="btn-gold w-full !py-3">
                {banner.enabled ? "Publish Offer Strip" : "Save & Keep Hidden"}
              </button>
              {notice && (
                <p data-testid="banner-saved-notice" className="text-center text-xs text-[#E6C265]">
                  {notice}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
